import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { 
  Bell, Database, AlertCircle, FileSpreadsheet, Upload, RefreshCw, Trash2, Loader2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import GardeUploadSection from './GardeUploadSection';
import ProductsUploadSection from './ProductsUploadSection';
import ReportsConfigSection from './ReportsConfigSection';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SettingsTab = ({ onNavigateToImport, isAdmin, isManager }) => {
  const { language } = useLanguage();
  const { token } = useAuth();
  
  const [dataStats, setDataStats] = useState(null);
  const [clearingData, setClearingData] = useState(false);
  const [clearDataDialogOpen, setClearDataDialogOpen] = useState(false);
  const [sendingNotifications, setSendingNotifications] = useState(false);
  
  const { 
    isSubscribed: pushSubscribed, 
    isSupported: pushSupported,
    isLoading: pushLoading,
    subscribe: subscribePush,
    unsubscribe: unsubscribePush,
    sendTestNotification
  } = usePushNotifications();

  const fetchDataStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/data-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDataStats(response.data);
    } catch (error) {
      console.error('Error fetching data stats:', error);
    }
  }, [token]);

  useEffect(() => {
    if (isAdmin) {
      fetchDataStats();
    }
  }, [isAdmin, fetchDataStats]);

  const handleTogglePushNotifications = async () => {
    if (pushSubscribed) {
      await unsubscribePush();
    } else {
      await subscribePush();
    }
  };

  const handleTestNotification = async () => {
    await sendTestNotification();
  };

  const handleSendRenewalNotifications = async () => {
    setSendingNotifications(true);
    try {
      const response = await axios.post(`${API_URL}/push/send-renewal-alerts`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === 'fr' 
        ? `${response.data.sent || 0} notifications envoyées`
        : `${response.data.sent || 0} notifications sent`
      );
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur lors de l\'envoi' : 'Error sending notifications');
    } finally {
      setSendingNotifications(false);
    }
  };

  const handleClearTestData = async () => {
    setClearingData(true);
    try {
      const response = await axios.delete(`${API_URL}/admin/clear-test-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === 'fr' 
        ? 'Données de test supprimées'
        : 'Test data cleared');
      setClearDataDialogOpen(false);
      fetchDataStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error clearing data');
    } finally {
      setClearingData(false);
    }
  };

  return (
    <div className="space-y-6 pb-24">
      {/* Push Notifications Card - Admin only */}
      {isAdmin && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-[#0EA5E9]" />
              {language === 'fr' ? 'Notifications Push' : 'Push Notifications'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {!pushSupported ? (
              <div className="flex items-center gap-3 p-4 bg-yellow-50 rounded-lg">
                <AlertCircle className="w-5 h-5 text-yellow-600" />
                <p className="text-yellow-800">
                  {language === 'fr' 
                    ? 'Les notifications push ne sont pas supportées par ce navigateur'
                    : 'Push notifications are not supported by this browser'}
                </p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-[#0F172A]">
                      {language === 'fr' ? 'État des notifications' : 'Notification status'}
                    </p>
                    <p className="text-sm text-[#78716C]">
                      {pushSubscribed 
                        ? (language === 'fr' ? 'Activées' : 'Enabled')
                        : (language === 'fr' ? 'Désactivées' : 'Disabled')
                      }
                    </p>
                  </div>
                  <Switch 
                    checked={pushSubscribed}
                    onCheckedChange={handleTogglePushNotifications}
                    disabled={pushLoading}
                  />
                </div>
                
                {pushSubscribed && (
                  <div className="flex gap-3">
                    <Button 
                      variant="outline" 
                      onClick={handleTestNotification}
                      disabled={pushLoading}
                    >
                      <Bell className="w-4 h-4 mr-2" />
                      {language === 'fr' ? 'Tester' : 'Test'}
                    </Button>
                    <Button
                      onClick={handleSendRenewalNotifications}
                      disabled={sendingNotifications}
                      className="bg-[#0EA5E9]"
                    >
                      {sendingNotifications && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      {language === 'fr' ? 'Envoyer les alertes de renouvellement' : 'Send renewal alerts'}
                    </Button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Data Management / Import Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5 text-[#0EA5E9]" />
            {isManager && !isAdmin 
              ? (language === 'fr' ? 'Imports' : 'Imports')
              : (language === 'fr' ? 'Gestion des données' : 'Data Management')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Import Produits Direct Upload */}
          <ProductsUploadSection onNavigateToImport={onNavigateToImport} />

          {/* Pharmacies de Garde Upload */}
          <GardeUploadSection />

          {/* Data Stats - Admin only */}
          {isAdmin && dataStats && (() => {
            // Le backend peut retourner soit un entier, soit un objet
            // `{total, test, deleted, active}`. On normalise en `.active` (ou la valeur si entier).
            const v = (x) => {
              if (x == null) return 0;
              if (typeof x === 'number') return x;
              if (typeof x === 'object') return x.active ?? x.total ?? 0;
              return 0;
            };
            return (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="p-4 bg-red-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-red-600">{v(dataStats.diagnoses)}</p>
                  <p className="text-sm text-red-800">{language === 'fr' ? 'Diagnostics' : 'Diagnoses'}</p>
                </div>
                <div className="p-4 bg-red-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-red-600">{v(dataStats.sales)}</p>
                  <p className="text-sm text-red-800">{language === 'fr' ? 'Ventes' : 'Sales'}</p>
                </div>
                <div className="p-4 bg-red-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-red-600">{v(dataStats.clients)}</p>
                  <p className="text-sm text-red-800">{language === 'fr' ? 'Clients' : 'Clients'}</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-600">{v(dataStats.users)}</p>
                  <p className="text-sm text-green-800">{language === 'fr' ? 'Utilisateurs' : 'Users'}</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-600">{v(dataStats.products)}</p>
                  <p className="text-sm text-green-800">{language === 'fr' ? 'Produits' : 'Products'}</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-600">{v(dataStats.supplements)}</p>
                  <p className="text-sm text-green-800">{language === 'fr' ? 'Compléments' : 'Supplements'}</p>
                </div>
              </div>
            );
          })()}
          
          {/* Reset data - Admin only */}
          {isAdmin && (
            <>
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-yellow-800">
                      {language === 'fr' ? 'Réinitialiser les données de test' : 'Reset test data'}
                    </p>
                    <p className="text-sm text-yellow-700 mt-1">
                      {language === 'fr' 
                        ? 'Cette action supprimera tous les diagnostics, ventes et clients. Les utilisateurs, produits et compléments seront conservés.'
                        : 'This will delete all diagnoses, sales and clients. Users, products and supplements will be preserved.'}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-3 justify-end">
                <Button
                  variant="outline"
                  onClick={fetchDataStats}
                  data-testid="refresh-data-stats"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {language === 'fr' ? 'Actualiser' : 'Refresh'}
                </Button>
                <Button
                  variant="destructive"
                  size="lg"
                  onClick={() => setClearDataDialogOpen(true)}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="clear-test-data-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {language === 'fr' ? 'SUPPRIMER LES DONNÉES DE TEST' : 'DELETE TEST DATA'}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Weekly Reports Config - Admin only */}
      {isAdmin && (
        <ReportsConfigSection />
      )}

      {/* Clear Data Confirmation Dialog */}
      <Dialog open={clearDataDialogOpen} onOpenChange={setClearDataDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">
              {language === 'fr' ? 'Confirmer la suppression' : 'Confirm deletion'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-[#78716C]">
              {language === 'fr' 
                ? 'Cette action est irréversible. Toutes les données de test (diagnostics, ventes, clients) seront supprimées.'
                : 'This action is irreversible. All test data (diagnoses, sales, clients) will be deleted.'}
            </p>
            <div className="p-3 bg-green-50 rounded-lg">
              <p className="text-sm text-green-800">
                {language === 'fr' 
                  ? 'Les utilisateurs, produits et compléments seront conservés.'
                  : 'Users, products and supplements will be preserved.'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClearDataDialogOpen(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleClearTestData}
              disabled={clearingData}
            >
              {clearingData && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {language === 'fr' ? 'Supprimer définitivement' : 'Delete permanently'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SettingsTab;
